import { useId, useLayoutEffect, type RefObject } from "react";
import type { ConnectionAnalyser } from "./ConnectionAnalyser";
import type { ConnectionEndpoint, ConnectionEndpointProvider, ConnectionEndpointSurface } from "./ConnectionEndpointProvider";
import { visibleControlPoint, buttonEditorScrollAnchor } from "./controlAnchorPoint";
import { controlExitDirection } from "./controlExitDirection";
import { collectConnectableControls } from "./connectableControlEndpoint";
import { measureEndpointContour } from "./endpointContour";

/** Resolves each connection to its own marked child control, never to the enclosing editor. */
export function useChildConnectionEndpoints(
  analyser: ConnectionAnalyser,
  root: RefObject<Element | null>,
  surface: ConnectionEndpointSurface,
): void {
  const id = useId();
  useLayoutEffect(() => {
    const provider: ConnectionEndpointProvider = {
      id,
      getConnectableEndpoints() {
        return collectConnectableControls(root.current, surface, (objectId) => state.objectRegistry.getObject(objectId));
      },
      getConnectionEndpoint(connection) {
        const endpoints: ConnectionEndpoint[] = [];
        for (const role of ["start", "end"] as const) {
          const object = role === "start" ? connection.fromDevice : connection.toDevice;
          if (!object || object.isDeleted) continue;
          const controls = root.current?.querySelectorAll(
            `[data-connection-object-id="${CSS.escape(object.id)}"]`,
          );
          if (!controls) continue;
          // Shared rockers can have two physical keys for one channel: use the first visible key.
          let scrollAnchor: ReturnType<typeof buttonEditorScrollAnchor>;
          let visible = false;
          for (const control of Array.from(controls)) {
            const point = visibleControlPoint(control);
            if (!point) {
              if (surface === "button-editor" && ["ButtonChannel", "Channel"].includes(object.className)) {
                scrollAnchor ??= buttonEditorScrollAnchor(control);
              }
              continue;
            }
            // Editor keys choose the edge facing the other endpoint after route selection.
            const exitDirection = surface === "button-editor" ? undefined : controlExitDirection(control);
            const contour = measureEndpointContour(control, point, surface);
            endpoints.push({ connectionId: connection.id, role, point, surface, representedObjectClass: object.className,
              exitDirection, contour, anchorObjectId: object.id });
            visible = true;
            break;
          }
          if (!visible && scrollAnchor) endpoints.push({
            connectionId: connection.id, role, point: scrollAnchor, surface,
            representedObjectClass: object.className, anchorObjectId: object.id,
            exitDirection: { x: -1, y: 0 },
          });
        }
        return endpoints;
      },
    };
    analyser.registerEndpointProvider(provider);
    const resize = new ResizeObserver(() => analyser.refreshEndpoints());
    if (root.current) resize.observe(root.current);
    return () => {
      resize.disconnect();
      analyser.unregisterEndpointProvider(provider);
    };
  }, [analyser, root, surface, id]);
}
